import { mirrorDigit } from "@/lib/lotteryLogic";
import { cn } from "@/lib/utils";

/**
 * Vertical rolling columns, one per seed digit.
 * Row 0 shows the seed itself. Clicking a cell toggles mirror for that cell.
 */
export const RollingWorkout = ({ columns, mirrorFlags, onToggleCell, dateSumDigit, anchorCells = [] }) => {
  if (!columns || columns.length === 0) return null;
  const anchorSet = new Set(anchorCells.map(([c, r]) => `${c}-${r}`));
  const mirrorSum = mirrorDigit(dateSumDigit);

  return (
    <div className="w-full overflow-x-auto">
      <div
        className="grid gap-3 min-w-max"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(72px, 1fr))` }}
        data-testid="rolling-workout-grid"
      >
        {columns.map((col, cIdx) => (
          <div key={cIdx} className="flex flex-col items-center gap-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-heading">
              Col {cIdx + 1}
            </div>
            {col.map((d, rIdx) => {
              const flagged = mirrorFlags?.[cIdx]?.[rIdx];
              const shown = flagged ? mirrorDigit(d) : d;
              const isSeedRow = rIdx === 0 || rIdx === col.length - 1;
              const isAnchorCell = anchorSet.has(`${cIdx}-${rIdx}`);
              const isDateSumAnchor = isAnchorCell && shown === dateSumDigit;
              const isMirrorAnchor = isAnchorCell && shown === mirrorSum && !isDateSumAnchor;
              return (
                <button
                  key={rIdx}
                  type="button"
                  data-testid={`cell-${cIdx}-${rIdx}`}
                  onClick={() => onToggleCell(cIdx, rIdx)}
                  className={cn(
                    "w-full h-14 rounded-lg border font-mono-digit text-2xl font-bold",
                    "transition-all duration-150 ease-out digit-pop relative",
                    "hover:scale-[1.04] hover:-translate-y-0.5",
                    isAnchorCell
                      ? "bg-emerald-500/20 border-emerald-400 text-emerald-200 shadow-[0_0_22px_rgba(16,185,129,0.45)]"
                      : isSeedRow
                        ? "bg-amber-500/10 border-amber-500/60 text-amber-300 shadow-[0_0_18px_rgba(245,158,11,0.25)]"
                        : "bg-[#131c2e]/80 border-slate-800 text-slate-100 hover:border-slate-600",
                    flagged && "ring-2 ring-purple-500/70 bg-purple-500/10 text-purple-200 border-purple-500/60",
                  )}
                  style={{ animationDelay: `${(cIdx * 10 + rIdx) * 15}ms` }}
                >
                  {isDateSumAnchor ? (
                    <span
                      data-testid={`cell-datesum-${cIdx}-${rIdx}`}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-emerald-300"
                    >
                      {shown}
                    </span>
                  ) : isMirrorAnchor ? (
                    <span
                      data-testid={`cell-mirror-${cIdx}-${rIdx}`}
                      className="inline-block border-b-[3px] border-emerald-300 leading-none pb-0.5"
                    >
                      {shown}
                    </span>
                  ) : (
                    shown
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-500">
        Seed anchors the <span className="text-amber-300">first and last row</span>. The{" "}
        <span className="text-emerald-300">circled digit</span> is today&apos;s date sum (
        {dateSumDigit}); the <span className="text-emerald-300 underline underline-offset-2 decoration-emerald-300 decoration-[3px]">underlined digit</span> is its mirror ({mirrorSum}). Every Possible Hit comes from a row,
        column, or diagonal that passes through one of these cells — straight or box, forward
        or reverse. Mirror substitution is applied automatically.
      </p>
    </div>
  );
};
